import React, { useState, useEffect, useCallback } from "react";
import {
  Settings,
  Users,
  User,
  Truck,
  Container,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Filter,
  Download,
  Upload,
  RefreshCw,
  MapPin,
  Phone,
  Copy,
  Archive,
  Warehouse,
  Database,
} from "lucide-react";
import { API_BASE_URL, API_ENDPOINTS } from "../constants/index";

// API Service Class
class ApiService {
  constructor(endpoint) {
    this.endpoint = endpoint;
    this.baseUrl = `${API_BASE_URL}${endpoint}`;
  }

  async get(id = null, params = {}) {
    try {
      const url = new URL(id ? `${this.baseUrl}/${id}` : this.baseUrl);
      Object.keys(params).forEach((key) =>
        url.searchParams.append(key, params[key])
      );

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${this.endpoint}:`, error);
      throw error;
    }
  }

  async post(data) {
    try {
      const response = await fetch(this.baseUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error(`Error creating ${this.endpoint}:`, error);
      throw error;
    }
  }

  async put(id, data) {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error(`Error updating ${this.endpoint}:`, error);
      throw error;
    }
  }

  async delete(id) {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error(`Error deleting ${this.endpoint}:`, error);
      throw error;
    }
  }
}

// API Services for each entity
// LIST APIs
const customersApi = new ApiService(API_ENDPOINTS.CUSTOMERMASTERLIST);
const vendorsApi = new ApiService(API_ENDPOINTS.VENDORMASTERLIST);
const warehousesApi = new ApiService(API_ENDPOINTS.WAREHOUSEMASTERLIST);
const vehiclesApi = new ApiService(API_ENDPOINTS.VEHICLEMASTERLIST);
const containersApi = new ApiService(API_ENDPOINTS.CONTAINERMASTERLIST);
const portsApi = new ApiService(API_ENDPOINTS.PORTMASTERLIST);
const transportersApi = new ApiService(API_ENDPOINTS.TRANSPORTERMASTERLIST);
const companiesApi = new ApiService(API_ENDPOINTS.COMPANYMASTERLIST);
// CREATE APIs
const customersApiCreate = new ApiService(API_ENDPOINTS.CUSTOMERMASTERCREATE);
const vendorsApiCreate = new ApiService(API_ENDPOINTS.VENDORMASTERCREATE);
const warehousesApiCreate = new ApiService(API_ENDPOINTS.WAREHOUSEMASTERCREATE);
const vehiclesApiCreate = new ApiService(API_ENDPOINTS.VEHICLEMASTERCREATE);
const containersApiCreate = new ApiService(API_ENDPOINTS.CONTAINERMASTERCREATE);
const portsApiCreate = new ApiService(API_ENDPOINTS.PORTMASTERCREATE);
const transportersApiCreate = new ApiService(
  API_ENDPOINTS.TRANSPORTERMASTERCREATE
);
const companiesApiCreate = new ApiService(API_ENDPOINTS.COMPANYMASTERCREATE);

// Data mapping functions - MOVED OUTSIDE COMPONENT
const mapCustomerData = (apiData) => {
  return apiData.map((item) => ({
    id: item.customer_id,
    name: item.customer_name,
    code: item.customer_code,
    location: item.city || item.address,
    address: item.address,
    contactPerson: item.poc_name,
    phone: item.ph_no,
    email: item.email,
    status: item.status === 1 ? "active" : "inactive",
    panNo: item.pan_no,
    gstNo: item.gst_no,
    state: item.state,
    city: item.city,
  }));
};

const mapVendorData = (apiData) => {
  return apiData.map((item) => ({
    id: item.vendor_id,
    name: item.vendor_name,
    code: item.vendor_code,
    location: item.city || item.address,
    address: item.address,
    contactPerson: item.poc_name,
    phone: item.ph_no,
    email: item.email,
    status: item.status === 1 ? "active" : "inactive",
    panNo: item.pan_no,
    gstNo: item.gst_no,
    state: item.state,
    city: item.city,
    pinCode: item.pin_code,
  }));
};

const mapWarehouseData = (apiData) => {
  return apiData.map((item) => ({
    id: item.wh_id,
    name: item.wh_name,
    code: item.wh_code,
    capacity: item.capacity,
    location: item.city || item.address,
    address: item.address,
    status: item.status === 1 ? "active" : "inactive",
    state: item.state,
    city: item.city,
    pincode: item.pincode,
    companyCode: item.company_code,
  }));
};

const mapVehicleData = (apiData) => {
  return apiData.map((item) => ({
    id: item.vehicle_id,
    number: item.vehicle_no,
    desc: item.desc,
    type: item.vehicle_type,
    capacity: item.max_capacity,
    transporterName: item.rt_transpoter_master?.name,
    driver: item.driver_name,
    driverNo: item.driver_contact_no,
    status: item.status === 1 ? "active" : "inactive",
    regNo: item.reg_no,
    gpsNo: item.gps_no,
    transporterCode: item.transpoter_code,
  }));
};

const mapContainerData = (apiData) => {
  return apiData.map((item) => ({
    id: item.container_id,
    number: item.container_code?.trim(),
    size: item.size,
    type: item.container_type,
    owner: item.company_name,
    status: item.wait?.toLowerCase() === "available" ? "available" : "in_use",
    capacity: item.max_capacity,
    regNo: item.reg_no?.trim(),
    pocName: item.poc_name,
    pocNo: item.poc_no,
    remarks: item.remarks,
  }));
};

const mapPortData = (apiData) => {
  return apiData.map((item) => ({
    id: item.port_id,
    name: item.port_name,
    code: item.port_code,
    location: item.city || item.address,
    address: item.address,
    state: item.state,
    city: item.city,
    pincode: item.pincode,
    status: item.status === 1 ? "active" : "inactive",
    lat: item.lat,
    long: item.long,
  }));
};

const mapTransporterData = (apiData) => {
  return apiData.map((item) => ({
    id: item.transpoter_id,
    name: item.name,
    code: item.transpoter_code,
    location: item.city || item.address,
    address: item.address,
    email: item.email,
    phone: item.ph_no,
    contactPerson: item.poc_name,
    contactPhone: item.poc_no,
    state: item.state,
    city: item.city,
    pincode: item.pincode,
    status: item.status === 1 ? "active" : "inactive",
    lat: item.lat,
    long: item.long,
  }));
};

const mapCompanyData = (apiData) => {
  return apiData.map((item) => ({
    id: item.company_code,
    name: item.company_name,
    code: item.company_code,
    status: item.status === 1 ? "active" : "inactive",
  }));
};

const Input = ({
  label,
  icon: Icon,
  className = "",
  value,
  onChange,
  ...props
}) => (
  <div className="space-y-1">
    {label && (
      <label className="block text-sm font-medium text-gray-700">{label}</label>
    )}
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
      )}
      <input
        className={`w-full ${Icon ? "pl-10" : ""} px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
        value={value}
        onChange={onChange}
        {...props}
      />
    </div>
  </div>
);

const Badge = ({ children, variant = "gray", size = "md", className = "" }) => {
  const variants = {
    gray: "bg-gray-100 text-gray-800",
    success: "bg-green-100 text-green-800",
    primary: "bg-blue-100 text-blue-800",
    warning: "bg-yellow-100 text-yellow-800",
    purple: "bg-purple-100 text-purple-800",
    orange: "bg-orange-100 text-orange-800",
  };

  const sizes = {
    sm: "px-2 py-0.5 text-xs",
    md: "px-2.5 py-0.5 text-xs",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {children}
    </span>
  );
};

const Button = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  onClick,
  disabled = false,
  ...props
}) => {
  const variants = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300",
    outline:
      "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100",
    danger: "bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300",
  };

  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-sm",
  };

  return (
    <button
      className={`inline-flex items-center rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

const Table = ({ columns, data, loading, emptyMessage }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="text-center text-gray-500">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} className="hover:bg-gray-50">
              {columns.map((column, colIndex) => (
                <td key={colIndex} className="px-6 py-4 whitespace-nowrap">
                  {column.render
                    ? column.render(row[column.accessor], row)
                    : row[column.accessor]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  totalItems,
}) => (
  <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg">
    <div className="flex items-center">
      <span className="text-sm text-gray-700">
        Showing {(currentPage - 1) * pageSize + 1} to{" "}
        {Math.min(currentPage * pageSize, totalItems)} of {totalItems} results
      </span>
    </div>
    <div className="flex items-center space-x-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Previous
      </Button>
      <span className="text-sm text-gray-700">
        Page {currentPage} of {totalPages}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
      </Button>
    </div>
  </div>
);

const Modal = ({ isOpen, onClose, title, children, size = "md" }) => {
  if (!isOpen) return null;

  const sizeClasses = {
    md: "max-w-md",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      <div
        className={`bg-white rounded-lg shadow-xl z-50 w-full mx-4 max-h-[90vh] overflow-y-auto ${sizeClasses[size]}`}
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
};

const FormInput = React.memo(
  ({ label, placeholder, value, onChange, type = "text", ...props }) => (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <input
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        type={type}
        placeholder={placeholder}
        value={value || ""}
        onChange={onChange}
        {...props}
      />
    </div>
  )
);

const MastersPage = () => {
  const [activeTab, setActiveTab] = useState("customers");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({
    status: "all",
    type: "all",
    location: "all",
  });

  // Data states for each entity
  const [customersData, setCustomersData] = useState([]);
  const [vendorsData, setVendorsData] = useState([]);
  const [warehousesData, setWarehousesData] = useState([]);
  const [vehiclesData, setVehiclesData] = useState([]);
  const [containersData, setContainersData] = useState([]);
  const [portsData, setPortsData] = useState([]);
  const [transportersData, setTransportersData] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);

  // Form data for adding new records
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tabs, setTabs] = useState([
    { id: "customers", label: "Customers", icon: Users },
    { id: "vendors", label: "Vendors", icon: User },
    { id: "warehouses", label: "Warehouses", icon: Warehouse },
    { id: "vehicles", label: "Vehicles", icon: Truck },
    { id: "containers", label: "Containers", icon: Container },
    { id: "ports", label: "Ports", icon: MapPin },
    { id: "transporters", label: "Transporters", icon: Truck },
    { id: "companies", label: "Companies", icon: Settings },
  ]);

  const get_tab_count = async () => {
    try {
      const url = new URL(API_BASE_URL + "/count/master/list");

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      let res = await response.json();
      console.log(`Response from API:`, res.data);

      if (res.data && Array.isArray(res.data)) {
        let all_Tabs = res.data.map((item) => {
          return {
            id: item.id,
            count: item.all,
            active: item.active,
          };
        });

        setTabs((prevTabs) => {
          return prevTabs.map((tab) => {
            const tabData = all_Tabs.find((t) => t.id === tab.id);
            return {
              ...tab,
              count: tabData ? tabData.count : 0,
              active: tabData ? tabData.active : 0,
            };
          });
        });
      } else {
        console.error("Invalid Tab count format:", res.data);
      }
    } catch (error) {
      console.error(`Error fetching:`, error);
      throw error;
    }
  };

  // API Services mapping List
  const apiServices = {
    customers: customersApi,
    vendors: vendorsApi,
    warehouses: warehousesApi,
    vehicles: vehiclesApi,
    containers: containersApi,
    ports: portsApi,
    transporters: transportersApi,
    companies: companiesApi,
  };
  // API Services mapping Create
  const apiServicesCreate = {
    customers: customersApiCreate,
    vendors: vendorsApiCreate,
    warehouses: warehousesApiCreate,
    vehicles: vehiclesApiCreate,
    containers: containersApiCreate,
    ports: portsApiCreate,
    transporters: transportersApiCreate,
    companies: companiesApiCreate,
  };

  const dataStates = {
    customers: [customersData, setCustomersData],
    vendors: [vendorsData, setVendorsData],
    warehouses: [warehousesData, setWarehousesData],
    vehicles: [vehiclesData, setVehiclesData],
    containers: [containersData, setContainersData],
    ports: [portsData, setPortsData],
    transporters: [transportersData, setTransportersData],
    companies: [companiesData, setCompaniesData],
  };

  // Load data based on active tab
  useEffect(() => {
    get_tab_count();
    loadData();
  }, [activeTab, currentPage, pageSize, searchQuery, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const apiService = apiServices[activeTab];
      const [, setData] = dataStates[activeTab];

      const params = {
        // page: currentPage,
        // limit: pageSize,
        // search: searchQuery,
        // ...filters
      };

      const response = await apiService.get(null, params);

      if (response.status === 200 && response.data) {
        let mappedData = [];

        // Map the data based on active tab
        switch (activeTab) {
          case "customers":
            mappedData = mapCustomerData(response.data);
            break;
          case "vendors":
            mappedData = mapVendorData(response.data);
            break;
          case "warehouses":
            mappedData = mapWarehouseData(response.data);
            break;
          case "vehicles":
            mappedData = mapVehicleData(response.data);
            break;
          case "containers":
            mappedData = mapContainerData(response.data);
            break;
          case "ports":
            mappedData = mapPortData(response.data);
            break;
          case "transporters":
            mappedData = mapTransporterData(response.data);
            break;
          case "companies":
            mappedData = mapCompanyData(response.data);
            break;
          default:
            mappedData = response.data;
        }

        setData(mappedData);
        setTotalRecords(mappedData.length);
      } else {
        throw new Error(response.message || "Failed to load data");
      }
    } catch (error) {
      console.error(`Error loading ${activeTab}:`, error);
      // Set fallback data or show error message
      const [, setData] = dataStates[activeTab];
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // Transform form data to match backend API expectations
  const transformPayload = (formData, entityType) => {
    const userId = localStorage.getItem("userId") || "1"; // Get user ID from storage

    switch (entityType) {
      case "customers":
        return {
          code: formData.code,
          name: formData.name,
          address: formData.address,
          state: formData.state,
          city: formData.city,
          lat: formData.lat,
          long: formData.long,
          panNo: formData.panNo,
          gstNo: formData.gstNo,
          contactPhone: formData.phone,
          contactPerson: formData.contactPerson,
          email: formData.email,
          created_by: userId
        };

      case "vendors":
        return {
          vendor_code: formData.code,
          vendor_name: formData.name,
          pan_no: formData.panNo,
          gst_no: formData.gstNo,
          poc_no: formData.phone,
          poc_name: formData.contactPerson,
          ph_no: formData.phone,
          email: formData.email,
          address: formData.address,
          state: formData.state,
          city: formData.city,
          pincode: formData.pincode,
          lat: formData.lat,
          long: formData.long,
          status: 1,
          created_by: userId
        };

      case "ports":
        return {
          port_code: formData.code,
          port_name: formData.name,
          state: formData.state,
          city: formData.city,
          pincode: formData.pincode,
          address: formData.address,
          lat: formData.lat,
          long: formData.long,
          status: 1,
          created_by: userId
        };

      case "transporters":
        return {
          transporter_code: formData.code,
          name: formData.name,
          poc_name: formData.pocName || formData.contactPerson,
          poc_no: formData.pocNo || formData.phone,
          address: formData.address,
          lat: formData.lat,
          long: formData.long,
          state: formData.state,
          city: formData.city,
          pincode: formData.pincode,
          email: formData.email,
          ph_no: formData.phone,
          status: 1,
          created_by: userId
        };

      case "warehouses":
        return {
          company_code: formData.companyCode,
          wh_code: formData.code,
          wh_name: formData.name,
          address: formData.address,
          state: formData.state,
          city: formData.city,
          capacity: formData.max_capacity || formData.capacity,
          pincode: formData.pincode,
          lat: formData.lat,
          long: formData.long,
          status: 1,
          created_by: userId
        };

      case "vehicles":
        return {
          transpoter_code: formData.transpoter_code || formData.transporterCode,
          vehicle_no: formData.vehicle_no || formData.number,
          vehicle_type: formData.vehicle_type || formData.type,
          reg_no: formData.reg_no || formData.regNo,
          desc: formData.desc,
          gps_no: formData.gps_no || formData.gpsNo,
          max_capacity: formData.max_capacity || formData.capacity,
          status: 1,
          created_by: userId,
          vehicle_name: formData.vehicle_name || "",
          driver_name: formData.driver_name || "",
          driver_contact_no: formData.driver_contact_no || ""
        };

      case "containers":
        return {
          container_code: formData.code,
          company_name: formData.owner || formData.company_name,
          poc_no: formData.pocNo,
          poc_name: formData.pocName,
          size: formData.size,
          wait: formData.wait,
          max_capacity: formData.max_capacity || formData.capacity,
          reg_no: formData.reg_no || formData.regNo,
          container_type: formData.container_type || formData.type,
          remarks: formData.remarks || "",
          status: 1,
          created_by: userId
        };

      case "companies":
        return {
          company_code: formData.code || formData.companyCode,
          company_name: formData.name,
          status: 1,
          created_by: userId
        };

      default:
        return { ...formData, created_by: userId };
    }
  };

  // Other handler functions remain the same...
  const handleCreate = async (data) => {
    try {
      setLoading(true);

      const apiService = apiServicesCreate[activeTab];
      const [currentData, setData] = dataStates[activeTab];

      // Transform the payload to match backend expectations
      const transformedData = transformPayload(data, activeTab);

      const response = await apiService.post(transformedData);

      if (response.status === 201 && response.data) {
        setData([response.data, ...currentData]);
        setShowAddModal(false);
        setFormData({});
        setErrors({});
        alert(response.message || "Record created successfully!");
      } else {
        throw new Error(response.message || "Failed to create record");
      }
    } catch (error) {
      console.error(`Error creating ${activeTab}:`, error);
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      setLoading(true);
      const apiService = apiServices[activeTab];
      const [currentData, setData] = dataStates[activeTab];

      const response = await apiService.put(id, data);

      if (response.success) {
        const updatedData = currentData.map((item) =>
          item.id === id ? response.data : item
        );
        setData(updatedData);
        alert("Record updated successfully!");
      } else {
        throw new Error(response.message || "Failed to update record");
      }
    } catch (error) {
      console.error(`Error updating ${activeTab}:`, error);
      alert(`Error updating record: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) {
      return;
    }

    try {
      setLoading(true);
      const apiService = apiServices[activeTab];
      const [currentData, setData] = dataStates[activeTab];

      const response = await apiService.delete(id);

      if (response.success) {
        const filteredData = currentData.filter((item) => item.id !== id);
        setData(filteredData);
        alert("Record deleted successfully!");
      } else {
        throw new Error(response.message || "Failed to delete record");
      }
    } catch (error) {
      console.error(`Error deleting ${activeTab}:`, error);
      alert(`Error deleting record: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const apiService = apiServices[activeTab];

      const response = await fetch(`${apiService.baseUrl}/export`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${activeTab}_export_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error("Export failed");
      }
    } catch (error) {
      console.error("Export error:", error);
      alert("Export failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (file) => {
    try {
      setLoading(true);
      const apiService = apiServices[activeTab];

      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${apiService.baseUrl}/import`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        loadData();
        alert(`Import successful! ${result.imported || 0} records imported.`);
      } else {
        throw new Error(result.message || "Import failed");
      }
    } catch (error) {
      console.error("Import error:", error);
      alert(`Import failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentData = () => {
    const [data] = dataStates[activeTab];
    return data;
  };

  const currentTabData = getCurrentData();

  // Filter and search logic
  const filteredData = currentTabData.filter((item) => {
    const matchesSearch =
      !searchQuery ||
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.number?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      filters.status === "all" || item.status === filters.status;

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(totalRecords / pageSize);

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        variant: "success",
        label: "Active",
        className: "bg-green-100 text-green-800",
      },
      inactive: {
        variant: "gray",
        label: "Inactive",
        className: "bg-gray-100 text-gray-800",
      },
      maintenance: {
        variant: "warning",
        label: "Maintenance",
        className: "bg-yellow-100 text-yellow-800",
      },
      under_repair: {
        variant: "warning",
        label: "Under Repair",
        className: "bg-yellow-100 text-yellow-800",
      },
      available: {
        variant: "success",
        label: "Available",
        className: "bg-green-100 text-green-800",
      },
      in_use: {
        variant: "primary",
        label: "In Use",
        className: "bg-blue-100 text-blue-800",
      },
      in_transit: {
        variant: "primary",
        label: "In Transit",
        className: "bg-blue-100 text-blue-800",
      },
      premium: {
        variant: "purple",
        label: "Premium",
        className: "bg-purple-100 text-purple-800",
      },
      standard: {
        variant: "gray",
        label: "Standard",
        className: "bg-gray-100 text-gray-800",
      },
    };

    const config = statusConfig[status] || {
      variant: "gray",
      label: status,
      className: "bg-gray-100 text-gray-800",
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}
      >
        {config.label}
      </span>
    );
  };

  // UPDATED Generic table component - using only available data fields
  const GenericTable = ({ data }) => {
    const getColumns = () => {
      switch (activeTab) {
        case "customers":
          return [
            {
              header: "Customer Details",
              accessor: "name",
              render: (_, item) => (
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">
                      {item.name}
                    </span>
                    <p className="text-sm text-gray-500">{item.code}</p>
                  </div>
                </div>
              ),
            },
            {
              header: "Contact & Location",
              accessor: "location",
              render: (_, item) => (
                <div>
                  <div className="flex items-center text-sm text-gray-900 mb-1">
                    <MapPin className="w-4 h-4 mr-1" />
                    {item.location}
                  </div>
                  <div className="flex items-center text-sm text-gray-600 mb-1">
                    <Phone className="w-4 h-4 mr-1" />
                    {item.phone}
                  </div>
                  <p className="text-sm text-gray-600">{item.contactPerson}</p>
                </div>
              ),
            },
            {
              header: "GST & PAN",
              accessor: "gstNo",
              render: (_, item) => (
                <div>
                  <p className="text-sm text-gray-900">GST: {item.gstNo}</p>
                  <p className="text-sm text-gray-600">PAN: {item.panNo}</p>
                </div>
              ),
            },
            {
              header: "Status",
              accessor: "status",
              render: (_, item) => getStatusBadge(item.status),
            },
            {
              header: "Actions",
              accessor: "actions",
              render: (_, item) => (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ),
            },
          ];
        case "vendors":
          return [
            {
              header: "Vendor Details",
              accessor: "name",
              render: (_, item) => (
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                    <User className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">
                      {item.name}
                    </span>
                    <p className="text-sm text-gray-500">{item.code}</p>
                  </div>
                </div>
              ),
            },
            {
              header: "Contact",
              accessor: "contactPerson",
              render: (_, item) => (
                <div>
                  <p className="text-sm text-gray-900">{item.contactPerson}</p>
                  <p className="text-sm text-gray-600">{item.phone}</p>
                  <p className="text-sm text-gray-600">{item.email}</p>
                </div>
              ),
            },
            {
              header: "Location",
              accessor: "location",
              render: (_, item) => (
                <div>
                  <div className="flex items-center text-sm text-gray-900">
                    <MapPin className="w-4 h-4 mr-1" />
                    {item.location}
                  </div>
                  <p className="text-sm text-gray-600">{item.state}</p>
                </div>
              ),
            },
            {
              header: "GST & PAN",
              accessor: "gstNo",
              render: (_, item) => (
                <div>
                  <p className="text-sm text-gray-900">GST: {item.gstNo}</p>
                  <p className="text-sm text-gray-600">PAN: {item.panNo}</p>
                </div>
              ),
            },
            {
              header: "Status",
              accessor: "status",
              render: (_, item) => getStatusBadge(item.status),
            },
            {
              header: "Actions",
              accessor: "actions",
              render: (_, item) => (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ),
            },
          ];
        case "warehouses":
          return [
            {
              header: "Warehouse Details",
              accessor: "name",
              render: (_, item) => (
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <Warehouse className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">
                      {item.name}
                    </span>
                    <p className="text-sm text-gray-500">{item.code}</p>
                  </div>
                </div>
              ),
            },
            {
              header: "Capacity",
              accessor: "capacity",
              render: (_, item) => (
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {item.capacity}
                  </p>
                  <p className="text-sm text-gray-600">
                    Company: {item.companyCode}
                  </p>
                </div>
              ),
            },
            {
              header: "Location",
              accessor: "location",
              render: (_, item) => (
                <div>
                  <div className="flex items-center text-sm text-gray-900">
                    <MapPin className="w-4 h-4 mr-1" />
                    {item.location}
                  </div>
                  <p className="text-sm text-gray-600">{item.state}</p>
                  <p className="text-sm text-gray-600">PIN: {item.pincode}</p>
                </div>
              ),
            },
            {
              header: "Status",
              accessor: "status",
              render: (_, item) => getStatusBadge(item.status),
            },
            {
              header: "Actions",
              accessor: "actions",
              render: (_, item) => (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ),
            },
          ];
        case "vehicles":
          return [
            {
              header: "Vehicle Details",
              accessor: "number",
              render: (_, item) => (
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <Truck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">
                      {item.number}
                    </span>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                    <Badge variant="gray" size="sm">
                      {item.type}
                    </Badge>
                  </div>
                </div>
              ),
            },
            {
              header: "Transporter",
              accessor: "transporter",
              render: (_, item) => (
                <div>
                  <p className="font-medium text-gray-900">{item.transporterName}</p>
                  <p className="text-sm text-gray-600">
                    Code: {item.transporterCode}
                  </p>
                </div>
              ),
            },
            {
              header: "Driver",
              accessor: "driver",
              render: (_, item) => (
                <div>
                  <p className="text-sm text-gray-600">
                    Name: {item.driver} 
                  </p>
                  <p className="text-sm text-gray-600">
                    Contact: {item.driverNo}
                  </p>
                </div>
              ),
            },
            {
              header: "Capacity & GPS",
              accessor: "capacity",
              render: (_, item) => (
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {item.capacity}
                  </p>
                  <p className="text-sm text-gray-600">GPS: {item.gpsNo}</p>
                </div>
              ),
            },
            {
              header: "Status",
              accessor: "status",
              render: (_, item) => getStatusBadge(item.status),
            },
            {
              header: "Actions",
              accessor: "actions",
              render: (_, item) => (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ),
            },
          ];
        case "containers":
          return [
            {
              header: "Container Details",
              accessor: "number",
              render: (_, item) => (
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                    <Container className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">
                      {item.number}
                    </span>
                    <p className="text-sm text-gray-500">{item.size}</p>
                    <Badge variant="gray" size="sm">
                      {item.type}
                    </Badge>
                  </div>
                </div>
              ),
            },
            {
              header: "Owner & Contact",
              accessor: "owner",
              render: (_, item) => (
                <div>
                  <p className="font-medium text-gray-900">{item.owner}</p>
                  <p className="text-sm text-gray-600">{item.pocName}</p>
                  <p className="text-sm text-gray-600">{item.pocNo}</p>
                </div>
              ),
            },
            {
              header: "Capacity",
              accessor: "capacity",
              render: (_, item) => (
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {item.capacity}
                  </p>
                  <p className="text-sm text-gray-600">Reg: {item.regNo}</p>
                </div>
              ),
            },
            {
              header: "Status",
              accessor: "status",
              render: (_, item) => getStatusBadge(item.status),
            },
            {
              header: "Actions",
              accessor: "actions",
              render: (_, item) => (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ),
            },
          ];
        case "ports":
          return [
            {
              header: "Port Details",
              accessor: "name",
              render: (_, item) => (
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">
                      {item.name}
                    </span>
                    <p className="text-sm text-gray-500">Code: {item.code}</p>
                  </div>
                </div>
              ),
            },
            {
              header: "Location",
              accessor: "location",
              render: (_, item) => (
                <div>
                  <div className="flex items-center text-sm text-gray-900">
                    <MapPin className="w-4 h-4 mr-1" />
                    {item.city}, {item.state}
                  </div>
                  <p className="text-sm text-gray-600">PIN: {item.pincode}</p>
                </div>
              ),
            },
            {
              header: "Address",
              accessor: "address",
              render: (_, item) => (
                <p className="text-sm text-gray-900">{item.address}</p>
              ),
            },
            {
              header: "Status",
              accessor: "status",
              render: (_, item) => getStatusBadge(item.status),
            },
            {
              header: "Actions",
              accessor: "actions",
              render: (_, item) => (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ),
            },
          ];
        case "transporters":
          return [
            {
              header: "Transporter Details",
              accessor: "name",
              render: (_, item) => (
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                    <Truck className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">
                      {item.name}
                    </span>
                    <p className="text-sm text-gray-500">Code: {item.code}</p>
                  </div>
                </div>
              ),
            },
            {
              header: "Contact",
              accessor: "contactPerson",
              render: (_, item) => (
                <div>
                  <p className="text-sm text-gray-900">{item.contactPerson}</p>
                  <p className="text-sm text-gray-600">{item.phone}</p>
                  <p className="text-sm text-gray-600">{item.email}</p>
                </div>
              ),
            },
            {
              header: "Location",
              accessor: "location",
              render: (_, item) => (
                <div>
                  <div className="flex items-center text-sm text-gray-900">
                    <MapPin className="w-4 h-4 mr-1" />
                    {item.city}, {item.state}
                  </div>
                  <p className="text-sm text-gray-600">PIN: {item.pincode}</p>
                </div>
              ),
            },
            {
              header: "Status",
              accessor: "status",
              render: (_, item) => getStatusBadge(item.status),
            },
            {
              header: "Actions",
              accessor: "actions",
              render: (_, item) => (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ),
            },
          ];
        case "companies":
          return [
            {
              header: "Company Details",
              accessor: "name",
              render: (_, item) => (
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                    <Settings className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">
                      {item.name}
                    </span>
                    <p className="text-sm text-gray-500">Code: {item.code}</p>
                  </div>
                </div>
              ),
            },
            {
              header: "Company Code",
              accessor: "code",
              render: (_, item) => <Badge variant="gray">{item.code}</Badge>,
            },
            {
              header: "Status",
              accessor: "status",
              render: (_, item) => getStatusBadge(item.status),
            },
            {
              header: "Actions",
              accessor: "actions",
              render: (_, item) => (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ),
            },
          ];
        default:
          return [];
      }
    };

    return (
      <Table
        columns={getColumns()}
        data={data}
        loading={loading}
        emptyMessage={`No ${activeTab} found`}
      />
    );
  };

  // Move this outside the main component, before MastersPage
  const FormInput = React.memo(
    ({ label, placeholder, value, onChange, type = "text", ...props }) => (
      <div className="space-y-1">
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <input
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          type={type}
          placeholder={placeholder}
          value={value || ""}
          onChange={onChange}
          {...props}
        />
      </div>
    )
  );

  // Replace the entire AddRecordForm component with this:
  const AddRecordForm = () => {
    // Individual state for each field to prevent re-renders
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [location, setLocation] = useState("");
    const [address, setAddress] = useState("");
    const [contactPerson, setContactPerson] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [panNo, setPanNo] = useState("");
    const [gstNo, setGstNo] = useState("");
    const [state, setState] = useState("");
    const [city, setCity] = useState("");
    const [pincode, setPincode] = useState("");
    const [capacity, setCapacity] = useState("");
    const [companyCode, setCompanyCode] = useState("");
    const [number, setNumber] = useState("");
    const [regNo, setRegNo] = useState("");
    const [desc, setDesc] = useState("");
    const [type, setType] = useState("");
    const [gpsNo, setGpsNo] = useState("");
    const [transporterCode, setTransporterCode] = useState("");
    const [size, setSize] = useState("");
    const [owner, setOwner] = useState("");
    const [pocName, setPocName] = useState("");
    const [pocNo, setPocNo] = useState("");
    const [wait, setWait] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [created_by, setCreated_by] = useState("Admin");
    const [lat, setLat] = useState("");
    const [long, setLong] = useState("");

    // Reset form when modal opens
    useEffect(() => {
      if (showAddModal) {
        setName("");
        setCode("");
        setLocation("");
        setAddress("");
        setContactPerson("");
        setPhone("");
        setEmail("");
        setPanNo("");
        setGstNo("");
        setState("");
        setCity("");
        setPincode("");
        setCapacity("");
        setCompanyCode("");
        setNumber("");
        setRegNo("");
        setDesc("");
        setType("");
        setGpsNo("");
        setTransporterCode("");
        setSize("");
        setOwner("");
        setPocName("");
        setPocNo("");
        setWait("");
        setContactPhone("");
      }
    }, [showAddModal]);

    const handleSubmit = (e) => {
      e.preventDefault();

      // Collect all form data based on active tab
      const formData = {
        name,
        code,
        location,
        address,
        contactPerson,
        phone,
        email,
        panNo,
        gstNo,
        state,
        city,
        pincode,
        max_capacity: capacity,
        companyCode,
        vehicle_no: number,
        reg_no: regNo,
        desc,
        vehicle_type: type,
        gps_no: gpsNo,
        transpoter_code: transporterCode,
        size,
        owner,
        pocName,
        pocNo,
        wait,
        contactPhone,
        lat,
        long,
        created_by,
        status: 1,
        vehicle_name: "",
      };

      handleCreate(formData);
    };

    const getFormFields = () => {
      switch (activeTab) {
        case "customers":
          return (
            <>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Name *"
                  placeholder="Enter customer name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <FormInput
                  label="Code *"
                  placeholder="Enter customer code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <FormInput
                label="Location *"
                placeholder="Enter location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
              <FormInput
                label="Address"
                placeholder="Enter full address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Contact Person *"
                  placeholder="Enter contact person"
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e.target.value)}
                />
                <FormInput
                  label="Phone *"
                  placeholder="+91-9876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <FormInput
                label="Email"
                placeholder="Enter email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="PAN Number"
                  placeholder="Enter PAN number"
                  value={panNo}
                  onChange={(e) => setPanNo(e.target.value)}
                />
                <FormInput
                  label="GST Number"
                  placeholder="Enter GST number"
                  value={gstNo}
                  onChange={(e) => setGstNo(e.target.value)}
                />
              </div>
            </>
          );
        case "vendors":
          return (
            <>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Name *"
                  placeholder="Enter vendor name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <FormInput
                  label="Code *"
                  placeholder="Enter vendor code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <FormInput
                label="Location *"
                placeholder="Enter location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
              <FormInput
                label="Address"
                placeholder="Enter full address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Contact Person *"
                  placeholder="Enter contact person"
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e.target.value)}
                />
                <FormInput
                  label="Phone *"
                  placeholder="+91-9876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <FormInput
                label="Email"
                placeholder="Enter email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="PAN Number"
                  placeholder="Enter PAN number"
                  value={panNo}
                  onChange={(e) => setPanNo(e.target.value)}
                />
                <FormInput
                  label="GST Number"
                  placeholder="Enter GST number"
                  value={gstNo}
                  onChange={(e) => setGstNo(e.target.value)}
                />
              </div>
            </>
          );
        case "warehouses":
          return (
            <>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Name *"
                  placeholder="Enter warehouse name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <FormInput
                  label="Code *"
                  placeholder="Enter warehouse code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Capacity *"
                  placeholder="e.g., 10000 sq mt"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                />
                <FormInput
                  label="Company Code"
                  placeholder="Enter company code"
                  value={companyCode}
                  onChange={(e) => setCompanyCode(e.target.value)}
                />
              </div>
              <FormInput
                label="Location *"
                placeholder="Enter location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
              <FormInput
                label="Address"
                placeholder="Enter full address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="State"
                  placeholder="Enter state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
                <FormInput
                  label="Pin Code"
                  placeholder="Enter pin code"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                />
              </div>
            </>
          );
        case "vehicles":
          return (
            <>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Vehicle Number *"
                  placeholder="e.g., MH-01-AB-1234"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                />
                <FormInput
                  label="Registration Number"
                  placeholder="Enter registration number"
                  value={regNo}
                  onChange={(e) => setRegNo(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Description"
                  placeholder="e.g., Tata Motors-LPT 1618"
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                />
                <FormInput
                  label="Vehicle Type"
                  placeholder="e.g., Truck"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Max Capacity"
                  placeholder="e.g., 16 Tons"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                />
                <FormInput
                  label="GPS Number"
                  placeholder="Enter GPS number"
                  value={gpsNo}
                  onChange={(e) => setGpsNo(e.target.value)}
                />
              </div>
              <FormInput
                label="Transporter Code"
                placeholder="Enter transporter code"
                value={transporterCode}
                onChange={(e) => setTransporterCode(e.target.value)}
              />
            </>
          );
        case "containers":
          return (
            <>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Container Code *"
                  placeholder="e.g., CONT001"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <FormInput
                  label="Registration Number"
                  placeholder="Enter registration number"
                  value={regNo}
                  onChange={(e) => setRegNo(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Size"
                  placeholder="e.g., 20FT, 40FT"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                />
                <FormInput
                  label="Container Type"
                  placeholder="e.g., 20FT, 40FT"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Company/Owner *"
                  placeholder="e.g., CIMC"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                />
                <FormInput
                  label="Max Capacity"
                  placeholder="e.g., 33.2 m³"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="POC Name"
                  placeholder="Enter POC name"
                  value={pocName}
                  onChange={(e) => setPocName(e.target.value)}
                />
                <FormInput
                  label="POC Number"
                  placeholder="Enter POC number"
                  value={pocNo}
                  onChange={(e) => setPocNo(e.target.value)}
                />
              </div>
              <FormInput
                label="Wait Status"
                placeholder="e.g., Available"
                value={wait}
                onChange={(e) => setWait(e.target.value)}
              />
            </>
          );
        case "ports":
          return (
            <>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Port Name *"
                  placeholder="Enter port name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <FormInput
                  label="Port Code *"
                  placeholder="Enter port code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <FormInput
                label="Address"
                placeholder="Enter full address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <div className="grid grid-cols-3 gap-4">
                <FormInput
                  label="City *"
                  placeholder="Enter city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
                <FormInput
                  label="State *"
                  placeholder="Enter state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
                <FormInput
                  label="Pin Code"
                  placeholder="Enter pin code"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                />
              </div>
            </>
          );
        case "transporters":
          return (
            <>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Transporter Name *"
                  placeholder="Enter transporter name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <FormInput
                  label="Transporter Code *"
                  placeholder="Enter transporter code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Email"
                  placeholder="Enter email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <FormInput
                  label="Phone *"
                  placeholder="+91-9876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="POC Name *"
                  placeholder="Enter POC name"
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e.target.value)}
                />
                <FormInput
                  label="POC Phone *"
                  placeholder="Enter POC phone"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </div>
              <FormInput
                label="Address"
                placeholder="Enter full address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <div className="grid grid-cols-3 gap-4">
                <FormInput
                  label="City *"
                  placeholder="Enter city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
                <FormInput
                  label="State *"
                  placeholder="Enter state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
                <FormInput
                  label="Pin Code"
                  placeholder="Enter pin code"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                />
              </div>
            </>
          );
        case "companies":
          return (
            <>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Company Name *"
                  placeholder="Enter company name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <FormInput
                  label="Company Code *"
                  placeholder="Enter company code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
            </>
          );
        default:
          return null;
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.general && (
          <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
            {errors.general}
          </div>
        )}

        {getFormFields()}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setShowAddModal(false);
              setFormData({});
              setErrors({});
            }}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading
              ? "Creating..."
              : `Create ${activeTab.slice(0, -1).charAt(0).toUpperCase() + activeTab.slice(1, -1)}`}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Database className="w-7 h-7 mr-3 text-blue-600" />
            Master Data Management
          </h1>
        </div>

        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleImport(file);
              }}
              className="hidden"
            />
            <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              <Upload className="w-4 h-4 mr-2" />
              Import Data
            </div>
          </label>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add New{" "}
            {activeTab.slice(0, -1).charAt(0).toUpperCase() +
              activeTab.slice(1, -1)}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-4">
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          const [data] = dataStates[tab.id];
          const activeData = data.filter(
            (item) => item.status === "active" || item.status === "available"
          );

          return (
            <div
              key={tab.id}
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                activeTab === tab.id
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
              onClick={() => {
                setActiveTab(tab.id);
                setCurrentPage(1);
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {tab.label}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tab.count}
                  </p>
                  <p className="text-xs text-gray-500">Active: {tab.active}</p>
                </div>
                <TabIcon
                  className={`w-8 h-8 ${
                    activeTab === tab.id ? "text-blue-600" : "text-gray-400"
                  }`}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <Input
              placeholder={`Search ${activeTab}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={Search}
              className="w-full sm:w-80"
            />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Total Records:</span>
              <span className="font-semibold ml-2">{totalRecords}</span>
            </div>
            <div>
              <span className="text-gray-500">Active:</span>
              <span className="font-semibold ml-2 text-green-600">
                {
                  currentTabData.filter(
                    (item) =>
                      item.status === "active" || item.status === "available"
                  ).length
                }
              </span>
            </div>
            <div>
              <span className="text-gray-500">Showing:</span>
              <span className="font-semibold ml-2">{filteredData.length}</span>
            </div>
            <div>
              <span className="text-gray-500">Page:</span>
              <span className="font-semibold ml-2">
                {currentPage} of {totalPages}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="space-y-4">
        <GenericTable data={filteredData} />

        {/* Pagination */}
        {totalRecords > pageSize && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalItems={totalRecords}
          />
        )}
      </div>

      {/* Add New Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setFormData({});
          setErrors({});
        }}
        title={`Add New ${activeTab.slice(0, -1).charAt(0).toUpperCase() + activeTab.slice(1, -1)}`}
        size="xl"
      >
        <AddRecordForm />
      </Modal>
    </div>
  );
};

export default MastersPage;
